<template>
  <div>
    <!-- Measurement summary (Lot 0) -->
    <v-card v-if="report" outlined class="mb-4">
      <v-card-text>
        <div class="d-flex flex-wrap" style="gap: 32px">
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.report.home-distribution') }}</div>
            <div v-for="s in ['HOME','TO_CONFIRM','AWAY','UNDETERMINED']" :key="s">
              <v-chip x-small :color="homeColor(s)" dark class="mr-1">{{ $t('meter-device.connected-vehicle.home.' + s) }}</v-chip>
              {{ report.homeDistribution[s].count }} ({{ report.homeDistribution[s].pct }}%)
            </div>
          </div>
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.report.position-availability') }}</div>
            <div class="text-h6">{{ report.positionAvailabilityPct }}%</div>
            <div class="caption grey--text">{{ report.total }} {{ $tc('meter-device.connected-vehicle.charge', 2).toLowerCase() }} · {{ report.withRealCharger }} {{ $t('meter-device.connected-vehicle.report.with-real') }}</div>
          </div>
          <!-- Learned battery State-of-Health, PER PROVIDER: the effective usable capacity we MEASURE from that
               provider's OWN charges vs the catalogue nominal. Drives the SoC→kWh conversion for that provider's
               charges only (never borrowed). "unmeasured" = SoC-only provider (e.g. Smartcar). -->
          <div v-if="report.sohByProvider && Object.keys(report.sohByProvider).length">
            <div class="overline d-flex align-center">
              {{ $t('meter-device.connected-vehicle.soh.title') }}
              <v-tooltip bottom max-width="340">
                <template #activator="{ on, attrs }">
                  <v-icon x-small class="ml-1" v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
                </template>
                <span>{{ $t('meter-device.connected-vehicle.soh.help') }}</span>
              </v-tooltip>
            </div>
            <div v-for="(s, p) in report.sohByProvider" :key="p" class="d-flex align-center" style="gap: 6px">
              <span class="caption font-weight-medium" style="min-width: 92px">{{ p }}</span>
              <template v-if="s.effectiveCapacityKwh != null">
                <span class="font-weight-medium">{{ s.sohPercent }}%</span>
                <span class="caption grey--text">{{ s.effectiveCapacityKwh }}/{{ s.nominalKwh }} kWh</span>
                <v-chip x-small :color="sohColor(s)" dark>
                  {{ $t('meter-device.connected-vehicle.soh.source.' + s.source) }} ·
                  {{ $t('meter-device.connected-vehicle.soh.confidence.' + s.confidence) }}
                </v-chip>
              </template>
              <span v-else class="caption grey--text">{{ $t('meter-device.connected-vehicle.soh.unmeasured') }}</span>
            </div>
          </div>
          <div>
            <div class="overline d-flex align-center">
              {{ $t('meter-device.connected-vehicle.report.estimator-accuracy') }}
              <v-tooltip bottom max-width="340">
                <template #activator="{ on, attrs }">
                  <v-icon x-small class="ml-1" v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
                </template>
                <span>{{ $t('meter-device.connected-vehicle.report.estimator-help') }}</span>
              </v-tooltip>
            </div>
            <table class="caption cv-estimators">
              <thead>
                <tr class="grey--text text--darken-1">
                  <th></th>
                  <th class="px-2 text-right font-weight-regular">{{ $t('meter-device.connected-vehicle.report.samples') }}</th>
                  <th class="px-2 text-right font-weight-regular">{{ $t('meter-device.connected-vehicle.report.mean-error') }}</th>
                  <th class="px-2 text-right font-weight-regular">
                    {{ $t('meter-device.connected-vehicle.report.bias') }}
                    <v-tooltip bottom max-width="300">
                      <template #activator="{ on, attrs }">
                        <v-icon x-small v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>{{ $t('meter-device.connected-vehicle.report.bias-help') }}</span>
                    </v-tooltip>
                  </th>
                  <th class="px-2 text-right font-weight-regular">
                    {{ $t('meter-device.connected-vehicle.report.suggested-coefficient') }}
                    <v-tooltip bottom max-width="300">
                      <template #activator="{ on, attrs }">
                        <v-icon x-small v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>{{ $t('meter-device.connected-vehicle.report.suggested-coefficient-help') }}</span>
                    </v-tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, k) in report.estimatorAccuracy" :key="k">
                  <td class="pr-3">{{ estimatorLabel(k) }}</td>
                  <td class="px-2 text-right">
                    {{ v.samples }}
                    <span
                        v-if="v.captureGaps"
                        class="orange--text"
                        :title="$t('meter-device.connected-vehicle.capture-gap-help')"
                    >(+{{ v.captureGaps }} {{ $t('meter-device.connected-vehicle.report.gaps') }})</span>
                  </td>
                  <td class="px-2 text-right">{{ v.mape === null ? '—' : v.mape + '%' }}</td>
                  <td class="px-2 text-right" :class="v.meanSignedPct === null ? 'grey--text' : (v.meanSignedPct < 0 ? 'red--text' : 'green--text')">
                    {{ v.meanSignedPct === null ? '—' : (v.meanSignedPct > 0 ? '+' : '') + v.meanSignedPct + '%' }}
                  </td>
                  <td class="px-2 text-right font-weight-bold primary--text">
                    {{ v.impliedCoefficient == null ? '—' : '× ' + v.impliedCoefficient }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Coverage / anti-omission (EV only): did we capture as much charge energy as the km driven required?
             A deficit means real charges are MISSING (connectivity gap, provider outage) → alert. -->
        <div v-if="report.coverage && report.coverage.applicable" class="mt-4 pt-3" style="border-top: 1px solid #EEE">
          <div class="overline d-flex align-center">
            {{ $t('meter-device.connected-vehicle.coverage.title') }}
            <v-tooltip bottom max-width="340">
              <template #activator="{ on, attrs }">
                <v-icon x-small class="ml-1" v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
              </template>
              <span>{{ $t('meter-device.connected-vehicle.coverage.help') }}</span>
            </v-tooltip>
          </div>
          <div class="d-flex flex-wrap align-center" style="gap: 24px">
            <div>
              <v-chip small :color="coverageColor(report.coverage.status)" dark>
                {{ $t('meter-device.connected-vehicle.coverage.status.' + report.coverage.status) }}
                <strong v-if="report.coverage.bestCoveragePct != null" class="ml-1">{{ report.coverage.bestCoveragePct }}%</strong>
              </v-chip>
            </div>
            <div class="caption grey--text">
              {{ report.coverage.kmDriven }} km · {{ report.coverage.consumptionKwhPer100Km }} kWh/100km ·
              {{ $t('meter-device.connected-vehicle.coverage.expected') }} {{ report.coverage.expectedKwh }} kWh
            </div>
            <div class="caption">
              <span v-for="(v, p) in report.coverage.byProvider" :key="p" class="mr-3">
                {{ p }}: <strong :class="v.coveragePct != null && v.coveragePct < 70 ? 'red--text' : 'green--text'">{{ v.coveragePct == null ? '—' : v.coveragePct + '%' }}</strong>
                ({{ v.capturedKwh }} kWh)
              </span>
            </div>
          </div>
        </div>

        <!-- Localized companion to the global coverage: the individual charges the km↔SoC balance proves we
             missed (odometer moved far more than the SoC drop can explain). The place to later DECLARE a charge
             manually with the suggested value pre-filled. -->
        <div v-if="report.suspectedMissingCharges && report.suspectedMissingCharges.length"
             class="missing-list mt-4 pa-3" style="background: rgba(255,152,0,0.08); border-left: 3px solid #ff9800; border-radius: 4px;">
          <div class="font-weight-medium mb-1 d-flex align-center" style="color: #e65100;">
            <v-icon small color="orange darken-2" class="mr-1">mdi-alert-outline</v-icon>{{ $t('meter-device.connected-vehicle.missing.title') }}
          </div>
          <v-simple-table dense class="transparent">
            <template #default>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('meter-device.connected-vehicle.missing.provider') }}</th>
                  <th class="text-left">{{ $t('meter-device.connected-vehicle.missing.window') }}</th>
                  <th class="text-right">{{ $t('meter-device.connected-vehicle.missing.km') }}</th>
                  <th class="text-right">{{ $t('meter-device.connected-vehicle.missing.suggested') }}</th>
                  <th class="text-left">{{ $t('meter-device.connected-vehicle.missing.why') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(m, i) in report.suspectedMissingCharges" :key="i">
                  <td class="font-weight-medium">{{ m.provider }}</td>
                  <td>{{ new Date(m.startedAt).toLocaleString() }} → {{ new Date(m.stoppedAt).toLocaleString() }}</td>
                  <td class="text-right">{{ Math.round(m.kmDriven) }} km</td>
                  <td class="text-right font-weight-medium orange--text text--darken-3">{{ m.suggestedKwh != null ? m.suggestedKwh + ' kWh' : (Math.round(m.missingPercent) + ' %') }}</td>
                  <td class="grey--text text--darken-2">{{ m.reason }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col cols="12" md="4">
        <v-select
            v-model="status"
            :label="$t('status')"
            clearable
            :items="[
              {text: $t('meter-device.connected-vehicle.status.OBSERVED'), value: 'OBSERVED'},
              {text: $t('meter-device.connected-vehicle.status.CONFIRMED'), value: 'CONFIRMED'},
              {text: $t('meter-device.connected-vehicle.status.CONVERTED'), value: 'CONVERTED'},
              {text: $t('meter-device.connected-vehicle.status.IGNORED'), value: 'IGNORED'},
            ]"
        ></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
            v-model="providerFilter"
            :label="$t('meter-device.connected-vehicle.provider')"
            :items="providerOptions"
            clearable
        ></v-select>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" md="5" class="d-flex align-center justify-end">
        <!-- On-demand reconstruction of this meter's charges from the ALREADY-collected readings (the cron
             meter-device-collect-cv-charges does the same for every meter automatically). It does NOT poll the
             provider — that's the telematics catch-up/webhooks. -->
        <v-tooltip bottom max-width="320">
          <template #activator="{ on, attrs }">
            <v-btn small color="primary" :loading="collecting" @click="collect" class="mr-2" v-on="on" v-bind="attrs">
              <v-icon left small>mdi-cached</v-icon>{{ $t('meter-device.connected-vehicle.collect') }}
            </v-btn>
          </template>
          <span>{{ $t('meter-device.connected-vehicle.collect-hint') }}</span>
        </v-tooltip>
        <v-btn small outlined :disabled="!charges.length" @click="exportCsv">
          <v-icon left small>mdi-download</v-icon>{{ $t('meter-device.connected-vehicle.report.export-csv') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Sessions at a glance: one BAR per session from start to stop, one row per provider, so duration is
         visible and the same physical charge lines up in time across providers. -->
    <v-card outlined class="mb-4">
      <v-card-text>
        <div class="d-flex justify-end mb-2">
          <v-btn-toggle v-model="chartRange" mandatory dense>
            <v-btn v-for="r in rangeOptions" :key="r.value" :value="r.value" small>{{ $t('vehicle.chart.window.' + r.value) }}</v-btn>
          </v-btn-toggle>
        </div>
        <highcharts v-if="sessionSeries.length || missingSeries.length" :options="sessionChartOptions" />
        <v-alert v-else type="info" text dense class="mb-0">{{ $t('vehicle.chart.no-data') }}</v-alert>
      </v-card-text>
    </v-card>

    <v-data-table
        :headers="headers"
        :items="filteredCharges"
        :items-per-page="15"
        :loading="loading"
    >
      <template #item.provider="{ item }">
        <v-chip small label outlined>{{ item.provider }}</v-chip>
        <!-- Detection tier: WEBHOOK = the window is bounded by observed charge start/stop events (live-grade);
             TELEMETRY_CATCHUP = inferred from the SoC curve alone — the "to confirm" tier. -->
        <div class="mt-1">
          <v-chip x-small label
                  :color="item.source === 'WEBHOOK' ? 'green lighten-4' : (item.source === 'TELEMETRY_CATCHUP' ? 'orange lighten-4' : 'grey lighten-3')"
                  :text-color="item.source === 'WEBHOOK' ? 'green darken-3' : (item.source === 'TELEMETRY_CATCHUP' ? 'orange darken-3' : 'grey darken-2')">
            {{ $t('meter-device.connected-vehicle.source.' + item.source) }}
          </v-chip>
        </div>
      </template>

      <template #item.period="{ item }">
        {{ new Date(item.startedAt).toLocaleString() }}<br>
        <span v-if="item.stoppedAt" class="grey--text">→ {{ new Date(item.stoppedAt).toLocaleString() }}</span>
        <v-chip v-else x-small color="orange lighten-4" text-color="orange darken-3">
          {{ $t('meter-device.connected-vehicle.running') }}
        </v-chip>
        <!-- What the charge DID, in the units the driver reads: how long it charged, how long the cable stayed
             in, how many percent and how many kilometres of range came back. -->
        <div v-if="item.summary" class="mt-1">
          <span v-if="item.summary.chargingSeconds != null" class="font-weight-medium">
            <v-icon x-small color="green">mdi-lightning-bolt</v-icon> {{ fmtDuration(item.summary.chargingSeconds) }}
          </span>
          <span
              v-if="item.summary.sessionSeconds != null && item.summary.sessionSeconds !== item.summary.chargingSeconds"
              class="caption grey--text ml-2"
              :title="$t('meter-device.connected-vehicle.session-duration-help')"
          >
            <v-icon x-small>mdi-power-plug</v-icon> {{ fmtDuration(item.summary.sessionSeconds) }}
          </span>
          <div class="caption">
            <span v-if="item.summary.socAddedPercent" class="blue--text text--darken-2">
              <!-- The two ends, not just the gain: "56 → 90" is what lets the reading be checked against the
                   car's own screen, and against whatever app the driver is holding. -->
              <template v-if="item.readings && item.readings.startSoc != null && item.readings.stopSoc != null">
                {{ Math.round(item.readings.startSoc) }} → {{ Math.round(item.readings.stopSoc) }} %
              </template>
              (+{{ Math.round(item.summary.socAddedPercent) }} %)
            </span>
            <span
                v-if="item.summary.rangeAddedKm != null"
                class="blue--text text--darken-2 ml-2"
                :title="item.summary.startRangeKm + ' → ' + item.summary.stopRangeKm + ' km'"
            >+{{ item.summary.rangeAddedKm }} km</span>
          </div>
        </div>
        <!-- The PLUGGED episode framing the charge — the CDR's own shape (a charge point meters from plug-in to
             plug-out). Shown only when observed, and only when it adds something to the charge window. -->
        <div v-if="item.pluggedInAt || item.unpluggedAt" class="caption grey--text">
          <v-icon x-small>mdi-power-plug</v-icon>
          <template v-if="item.pluggedInAt">{{ fmtTime(item.pluggedInAt) }}</template><template v-else>?</template>
          →
          <template v-if="item.unpluggedAt">{{ fmtTime(item.unpluggedAt) }}</template><template v-else>?</template>
        </div>
        <!-- Observed charging spells inside the plugged episode: the vehicle stays plugged the whole window,
             but the charge itself pauses/resumes — showing the "on" periods reads a pause as "plugged, not
             charging", never as a data hole. Only shown when there actually was a pause (2+ spells). -->
        <div v-if="(item.chargingIntervals || []).length > 1" class="caption grey--text">
          <div v-for="(iv, i) in item.chargingIntervals" :key="i">
            <v-icon x-small color="green">mdi-lightning-bolt</v-icon>
            {{ fmtTime(iv.start) }}–{{ fmtTime(iv.stop) }}
          </div>
        </div>
      </template>

      <template #item.retained="{ item }">
        <strong>{{ fmtKwh(item.energy.retainedKwh) }}</strong>
        <!-- The headline gap: retained (base × applied coefficient) vs the real charger. Must be < 2%. A charge
             the provider only partially captured (SoC slice of a big charge) is flagged as a capture gap, not a
             loss error, so it isn't read as a red result. -->
        <v-chip
            v-if="isCaptureGap(item)"
            x-small color="orange lighten-4" text-color="orange darken-3" class="ml-1"
            :title="$t('meter-device.connected-vehicle.capture-gap-help')"
        >{{ $t('meter-device.connected-vehicle.capture-gap') }}</v-chip>
        <span
            v-else-if="retainedGapPct(item) !== null"
            class="caption ml-1"
            :class="Math.abs(retainedGapPct(item)) <= 2 ? 'green--text' : 'red--text'"
        >{{ fmtSignedPct(retainedGapPct(item)) }}</span>
        <div v-if="item.energy.retainedEstimator" class="caption grey--text">
          {{ estimatorLabelOf(item.energy.retainedEstimator) }}
        </div>
        <!-- Transparent breakdown: additive for SoC (conversion·SoC + aux·duration), multiplicative otherwise. -->
        <div v-if="retainedBreakdown(item)" class="caption grey--text">
          {{ retainedBreakdown(item) }}
        </div>
      </template>

      <!-- Only the estimators actually available for this charge, as compact chips (the retained one filled).
           Under each, its % gap vs the real charger (must be < 2%). The full four-way detail lives in the
           curve pop-up (eye icon). -->
      <template #item.estimators="{ item }">
        <div v-for="e in availableEstimators(item)" :key="e.key" class="mb-1">
          <v-chip
              x-small label
              :color="e.retained ? 'primary' : undefined"
              :text-color="e.retained ? 'white' : undefined"
              :outlined="!e.retained"
              :title="estimatorHint(e)"
          >
            {{ estimatorLabelOf(e.key) }}: {{ fmtKwh(e.value) }}<template v-if="e.decimal"> ~</template>
          </v-chip>
          <!-- The gap is measured on the loss-CORRECTED value (SoC additive, others × coefficient), computed
               server-side. We show the corrected kWh when it differs from the raw so the number is not a black box. -->
          <span v-if="gapPct(item, e.key, e.value) !== null" class="caption ml-1" :class="gapClass(item, e.key, e.value)">
            <template v-if="correctedDiffers(item, e.key, e.value)">→ {{ fmtKwh(correctedValue(item, e.key, e.value)) }} </template>{{ fmtSignedPct(gapPct(item, e.key, e.value)) }}
          </span>
        </div>
        <span v-if="!availableEstimators(item).length" class="grey--text caption">—</span>
      </template>

      <!-- The loss coefficient actually applied to turn the battery-side estimate into a grid-side (wall)
           value. Battery-side estimators only; the power integral is already grid-side (no correction). -->
      <template #header.loss="{ header }">
        {{ header.text }}
        <v-tooltip bottom max-width="300">
          <template #activator="{ on, attrs }">
            <v-icon x-small class="ml-1" v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
          </template>
          <span>{{ $t('meter-device.connected-vehicle.loss-applied-help') }}</span>
        </v-tooltip>
      </template>
      <template #item.loss="{ item }">
        <template v-if="isBatterySideRetained(item)">
          <div><strong>× {{ item.energy.lossCoefficient }}</strong></div>
          <!-- The coefficient that WOULD align this charge with the borne (borne ÷ battery-side base). Watching
               its spread across charges is how we decide whether calibration is constant or brand/duration-based. -->
          <div v-if="idealCoefficient(item) !== null && !isCaptureGap(item)" class="caption primary--text">
            {{ $t('meter-device.connected-vehicle.loss-ideal') }} × {{ idealCoefficient(item) }}
          </div>
        </template>
        <span v-else class="grey--text">—</span>
      </template>

      <!-- Real charger energy (the borne = ground truth), auto-matched from the vehicle's CDR. Read-only:
           a study-phase reference to score the estimators, never entered by hand and gone in the final product. -->
      <template #header.realCharger="{ header }">
        {{ header.text }}
        <v-tooltip bottom max-width="300">
          <template #activator="{ on, attrs }">
            <v-icon x-small class="ml-1" v-on="on" v-bind="attrs">mdi-help-circle-outline</v-icon>
          </template>
          <span>{{ $t('meter-device.connected-vehicle.real-charger-help') }}</span>
        </v-tooltip>
      </template>
      <template #item.realCharger="{ item }">
        <!-- A borne reading is only COMPARABLE when its window(s) coincide with the charge (coverage ≥ 0.9).
             Below that the two describe different periods (a merged later top-up, a borne that split the night)
             and a red % would accuse a correct model — so the value shows muted, flagged non-comparable, and no
             error percentages are derived from it anywhere on the row. -->
        <template v-if="item.energy.realChargerKwh != null">
          <strong v-if="borneComparable(item)">{{ fmtKwh(item.energy.realChargerKwh) }}</strong>
          <span v-else class="grey--text">
            {{ fmtKwh(item.energy.realChargerKwh) }}
            <div class="caption">{{ $t('meter-device.connected-vehicle.borne-not-comparable') }}</div>
          </span>
          <!-- When the borne total SUMS several CDRs (a cut-and-restart splits one physical charge), the
               addition is shown, or the reader finds "three different values" in the CDR screen and
               distrusts the total. One session = nothing extra, the total IS the session. -->
          <div v-if="(item.energy.realChargerSessions || []).length > 1" class="caption grey--text">
            <div v-for="(s, i) in item.energy.realChargerSessions" :key="i">
              {{ fmtTime(s.start) }}–{{ fmtTime(s.stop) }} : {{ fmtKwh(s.kwh) }}
            </div>
          </div>
        </template>
        <span v-else class="grey--text">—</span>
      </template>

      <!-- WHERE the charge happened, in one clear line: Domicile (home borne — the org's borne installed at the
           employee's home) / Bureau (an office borne, tied to no person) / Borne publique (our network) / Borne
           inconnue (an address we reverse-geocoded but no known borne) / Position indisponible (no fix at all).
           Under it, the address, and — for a known place — the detection evidence (distance + how long before the
           start / after the end the fix was). For an unknown borne, the raw coordinates. -->
      <template #item.home="{ item }">
        <div class="d-flex align-start">
          <v-icon small :color="placeColor((item.place || {}).kind)" class="mr-2 mt-1">{{ placeIcon((item.place || {}).kind) }}</v-icon>
          <div>
            <div class="font-weight-medium" :class="{ 'grey--text': ['UNKNOWN', 'GEOCODED'].includes((item.place || {}).kind) }">
              {{ $t('meter-device.connected-vehicle.place.' + ((item.place && item.place.kind) || 'UNKNOWN')) }}
            </div>
            <div v-if="item.place && item.place.label" class="caption grey--text">{{ item.place.label }}</div>
            <!-- An unknown (geocoded) borne always shows its raw coordinates — the operator's rule. Click to see
                 the exact point on a map. -->
            <div v-if="(item.place || {}).kind === 'GEOCODED' && item.place.latitude != null"
                 class="caption blue--text" style="cursor: pointer;" @click="placeMapCharge = item">
              <v-icon x-small class="mr-1" color="blue">mdi-crosshairs-gps</v-icon>{{ item.place.latitude.toFixed(6) }}, {{ item.place.longitude.toFixed(6) }}
            </div>
            <!-- For an identified place: how far the fix was + how long before the start / after the end. -->
            <div v-if="detectionLabel(item)" class="caption grey--text">{{ detectionLabel(item) }}</div>
          </div>
        </div>
      </template>

      <template #item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" :outlined="item.status === 'OBSERVED'" :dark="item.status !== 'OBSERVED'">
          {{ $t('meter-device.connected-vehicle.status.' + item.status) }}
        </v-chip>
      </template>

      <!-- Observation phase: no CDR is created from a reconstructed charge. The only action is to
           inspect the SoC/energy curve behind the reconstruction. -->
      <template #item.actions="{ item }">
        <v-btn icon small :title="$t('meter-device.connected-vehicle.curve.title')" @click="curveCharge = item">
          <v-icon small>mdi-chart-line</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <connected-vehicle-charge-curve-dialog
        v-if="curveCharge"
        :charge="curveCharge"
        @close="curveCharge = null"
    />

    <connected-vehicle-charge-place-map-dialog
        v-if="placeMapCharge && placeMapCharge.place && placeMapCharge.place.latitude != null"
        :latitude="placeMapCharge.place.latitude"
        :longitude="placeMapCharge.place.longitude"
        :label="placeMapCharge.place.label"
        @close="placeMapCharge = null"
    />
  </div>
</template>

<script>
import MeterDeviceRepository from "@repository/MeterDeviceRepository";
import ConnectedVehicleChargeCurveDialog from "./ConnectedVehicleChargeCurveDialog.vue";
import ConnectedVehicleChargePlaceMapDialog from "./ConnectedVehicleChargePlaceMapDialog.vue";
import Highcharts from "highcharts";
import xrange from "highcharts/modules/xrange";
import dayjs from "dayjs";

// xrange = horizontal time bars (a session drawn from its start to its stop). Init once on the shared instance.
if (!Highcharts.seriesTypes.xrange) {
  xrange(Highcharts);
}

export default {
  name: 'ConnectedVehicleCharges',

  components: { ConnectedVehicleChargeCurveDialog, ConnectedVehicleChargePlaceMapDialog },

  data() {
    return {
      meterDevice: null,
      charges: [],
      report: null,
      placeMapCharge: null,
      loading: false,
      collecting: false,
      status: null,
      curveCharge: null,
      providerFilter: null,
      providerColors: { SMARTCAR: '#2563EB', VOLTERAS: '#059669', LINK_BY_CAR: '#D97706' },
      chartRange: 'w1',
      rangeOptions: [
        { value: 'd1', amount: 1, unit: 'day' },
        { value: 'w1', amount: 1, unit: 'week' },
        { value: 'm1', amount: 1, unit: 'month' },
        { value: 'y1', amount: 1, unit: 'year' },
        { value: 'all', amount: null },
      ],
    }
  },

  computed: {
    providerOptions() {
      return [...new Set(this.charges.map(c => c.provider).filter(Boolean))].sort()
    },

    // Provider filter drives both the table and the sessions chart (client-side; all charges are loaded).
    filteredCharges() {
      return this.providerFilter ? this.charges.filter(c => c.provider === this.providerFilter) : this.charges
    },

    // Providers shown as chart rows (respecting the provider filter).
    chartProviders() {
      return this.providerFilter ? [this.providerFilter] : this.providerOptions
    },

    // Charges within the selected time window (client-side; all are loaded).
    chartCharges() {
      const range = this.rangeOptions.find(r => r.value === this.chartRange)
      if (!range || !range.amount) return this.charges
      const from = dayjs().subtract(range.amount, range.unit).valueOf()
      return this.charges.filter(c => new Date(c.startedAt).getTime() >= from)
    },

    // One xrange series per provider (its own row): each session a BAR from startedAt to stoppedAt.
    sessionSeries() {
      const providers = this.chartProviders
      return providers.map((provider, idx) => ({
        name: provider,
        color: this.providerColors[provider] || '#6B7280',
        pointWidth: 12,
        data: this.chartCharges
            .filter(c => c.provider === provider && c.stoppedAt)
            .map(c => ({
              x: new Date(c.startedAt).getTime(),
              x2: new Date(c.stoppedAt).getTime(),
              y: idx,
              energy: c.energy.retainedKwh,
            })),
      })).filter(s => s.data.length > 0)
    },

    // Suspected MISSING charges as RED bars in the SAME provider row: the km↔SoC balance proves a charge happened
    // there that this provider never captured. Red so it reads apart from the real (coloured) sessions; sits in
    // the gap between them. Respects the provider filter, like the sessions.
    missingSeries() {
      const providers = this.chartProviders
      const missing = (this.report && this.report.suspectedMissingCharges) || []
      const filtered = this.providerFilter ? missing.filter(m => m.provider === this.providerFilter) : missing
      return providers.map((provider, idx) => ({
        name: provider,
        color: '#E53935',
        pointWidth: 12,
        data: filtered
            .filter(m => m.provider === provider && m.startedAt && m.stoppedAt)
            .map(m => ({
              x: new Date(m.startedAt).getTime(),
              x2: new Date(m.stoppedAt).getTime(),
              y: idx,
              missing: true,
              energy: m.suggestedKwh,
            })),
      })).filter(s => s.data.length > 0)
    },

    sessionChartOptions() {
      const providers = this.chartProviders
      return {
        chart: { type: 'xrange', height: 80 + providers.length * 70, zoomType: 'x' },
        time: { useUTC: false }, // local time, consistent with the table
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: { type: 'datetime' },
        yAxis: { title: { text: null }, categories: providers, reversed: true, min: 0, max: Math.max(0, providers.length - 1) },
        tooltip: {
          headerFormat: '',
          pointFormatter() {
            const kwh = this.energy == null ? '—' : (Math.round(this.energy * 100) / 100) + ' kWh'
            const f = Highcharts.dateFormat
            const head = this.missing
              ? `<b style="color:#E53935">${this.series.name} — charge manquante</b><br>~${kwh}`
              : `<b>${this.series.name}</b><br>${kwh}`
            return `${f('%d/%m %H:%M', this.x)} → ${f('%H:%M', this.x2)}<br>${head}`
          },
        },
        plotOptions: {
          xrange: {
            borderRadius: 2, borderWidth: 0, minPointLength: 3,
            dataLabels: {
              enabled: true,
              formatter() { return this.point.energy ? (Math.round(this.point.energy * 10) / 10) : '' },
              style: { fontSize: '10px', textOutline: 'none' },
            },
          },
        },
        series: [...this.sessionSeries, ...this.missingSeries],
      }
    },

    // Table view: only the estimators that actually have a value (SmartCar/Renault often gives just SoC).
    availableEstimators: () => (item) => [
      { key: 'ENERGY_ADDED', value: item.energy.energyAddedKwh },
      { key: 'ENERGY_REMAINING', value: item.energy.energyRemainingDeltaKwh },
      // The decimal value is the tick-cadence refinement LAYER; the raw integer-based value stays visible in
      // the tooltip logic through the readings (start/stop SoC) — the layer is what the retention prefers.
      { key: 'SOC', value: item.energy.socDeltaDecimalKwh != null ? item.energy.socDeltaDecimalKwh : item.energy.socDeltaKwh, decimal: item.energy.socDeltaDecimalKwh != null },
      { key: 'POWER_DURATION', value: item.energy.powerDurationKwh },
      // La lecture du concurrent sur la même charge : intégrale de puissance quand la courbe est dense,
      // sinon ΔSoC × capacité BRUTE avec le pourcent entamé — et aucun modèle de perte, dans les deux cas.
      // Jamais retenue : elle est là pour que notre nombre se lise à côté de celui que le conducteur voit.
      { key: 'BENCHMARK', value: item.energy.benchmarkKwh, comparison: true, source: item.energy.benchmarkSource },
    ].filter(e => e.value != null).map(e => ({ ...e, retained: e.key === item.energy.retainedEstimator })),

    headers() {
      return [
        { text: this.$t('meter-device.connected-vehicle.provider'), value: 'provider' },
        { text: this.$t('meter-device.connected-vehicle.period'), value: 'period' },
        { text: this.$t('meter-device.connected-vehicle.retained-energy'), value: 'retained' },
        { text: this.$t('meter-device.connected-vehicle.loss-applied'), value: 'loss', sortable: false },
        { text: this.$t('meter-device.connected-vehicle.estimators'), value: 'estimators', sortable: false },
        { text: this.$t('meter-device.connected-vehicle.real-charger'), value: 'realCharger', sortable: false },
        { text: this.$t('meter-device.connected-vehicle.home-title'), value: 'home', sortable: false },
        { text: this.$t('status'), value: 'status' },
        { text: '', value: 'actions', width: 60, sortable: false },
      ]
    },
  },

  watch: {
    status() {
      this.loadData()
    },
  },

  mounted() {
    this.meterDevice = this.$parent.meterDevice
    this.loadData()
    this.loadReport()
  },

  methods: {
    loadData() {
      if (!this.meterDevice) return
      this.loading = true
      MeterDeviceRepository.searchConnectedVehicleCharges(this.meterDevice.id, this.status)
          .then(charges => {
            this.charges = charges
            this.loading = false
          }).catch(err => {
            this.loading = false
            this.notifyError(err)
          })
    },

    loadReport() {
      if (!this.meterDevice) return
      MeterDeviceRepository.getConnectedVehicleChargesReport(this.meterDevice.id)
          .then(report => { this.report = report })
          .catch(() => { this.report = null })
    },

    collect() {
      if (!this.meterDevice) return
      this.collecting = true
      MeterDeviceRepository.collectConnectedVehicleCharges(this.meterDevice.id).then(res => {
        this.collecting = false
        this.notifySuccess(this.$t('meter-device.connected-vehicle.collect-done', { count: res.created }))
        this.loadData()
        this.loadReport()
      }).catch(err => {
        this.collecting = false
        this.notifyError(err)
      })
    },

    exportCsv() {
      // The export is the deliverable of the measurement phase: it must carry the provider, the raw
      // endpoint readings and all four estimators, so the same physical charge can be compared line by line.
      const cols = ['id', 'vin', 'provider', 'source', 'externalChargeId', 'startedAt', 'stoppedAt',
        'startSoc', 'stopSoc', 'startEnergyRemainingKwh', 'stopEnergyRemainingKwh', 'startOdometerKm', 'stopOdometerKm',
        'energyAddedKwh', 'energyRemainingDeltaKwh', 'socDeltaKwh', 'powerDurationKwh',
        'retainedBaseKwh', 'lossCoefficient', 'retainedKwh', 'retainedEstimator', 'realChargerKwh',
        'homeStatus', 'homeScore', 'geolocated', 'distanceM', 'meterConfirmedHome',
        'distanceBeforeM', 'deltaBeforeSeconds', 'distanceAfterM', 'deltaAfterSeconds', 'status', 'cdrId']
      const rows = this.charges.map(c => [
        c.id, c.vin, c.provider, c.source, c.externalChargeId, c.startedAt, c.stoppedAt,
        c.readings.startSoc, c.readings.stopSoc, c.readings.startEnergyRemainingKwh, c.readings.stopEnergyRemainingKwh,
        c.readings.startOdometerKm, c.readings.stopOdometerKm,
        c.energy.energyAddedKwh, c.energy.energyRemainingDeltaKwh, c.energy.socDeltaKwh, c.energy.powerDurationKwh,
        c.energy.retainedBaseKwh, c.energy.lossCoefficient, c.energy.retainedKwh, c.energy.retainedEstimator, c.energy.realChargerKwh,
        c.home.status, c.home.score, c.home.geolocated, c.home.distanceM, c.home.meterConfirmedHome,
        c.home.distanceBeforeM, c.home.deltaBeforeSeconds, c.home.distanceAfterM, c.home.deltaAfterSeconds,
        c.status, c.cdrId,
      ])
      const escape = v => (v === null || v === undefined) ? '' : `"${String(v).replace(/"/g, '""')}"`
      const csv = [cols.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `connected-vehicle-charges-${this.meterDevice.id}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },

    homeColor(status) {
      return {
        HOME: 'green',
        TO_CONFIRM: 'orange',
        AWAY: 'blue-grey',
        UNDETERMINED: 'grey',
      }[status] || 'grey'
    },

    // "Rattaché à …": a known location shows kind (Domicile / Bureau) + its ADDRESS (never its name); an
    // unmatched charge shows the reverse-geocoded address alone. Never coordinates.
    matchedLabel(m) {
      if (!m) return this.$t('meter-device.connected-vehicle.matched.none')
      if (!m.type) return m.address || this.$t('meter-device.connected-vehicle.matched.none')
      const kind = m.type === 'HOME'
        ? this.$t('meter-device.connected-vehicle.matched.home')
        : this.$t('meter-device.connected-vehicle.matched.office')
      return m.address ? `${kind} — ${m.address}` : kind
    },

    coverageColor(status) {
      return { OK: 'green', MISSING: 'red', EXCESS: 'orange', UNKNOWN: 'grey' }[status] || 'grey'
    },

    /** SoH chip colour = how much we trust the measured capacity (energy anchor > power > none). */
    sohColor(soh) {
      return { high: 'green', medium: 'teal', low: 'orange', none: 'grey' }[soh.confidence] || 'grey'
    },

    placeColor(kind) {
      return { HOME: 'green', OFFICE: 'blue', PUBLIC_STATION: 'purple', GEOCODED: 'blue-grey', UNKNOWN: 'grey' }[kind] || 'grey'
    },

    placeIcon(kind) {
      return {
        HOME: 'mdi-home', OFFICE: 'mdi-office-building', PUBLIC_STATION: 'mdi-ev-station',
        GEOCODED: 'mdi-map-marker', UNKNOWN: 'mdi-map-marker-question',
      }[kind] || 'mdi-map-marker-question'
    },

    /**
     * One evidence line for an identified place: how far the fix was, and how long BEFORE the charge started
     * (side 'before') or AFTER it ended (side 'after') the fix was taken. Null when nothing was identified.
     */
    detectionLabel(item) {
      const d = item.place && item.place.detection
      if (!d || !d.side) return null
      const mins = d.deltaSeconds != null ? Math.round(d.deltaSeconds / 60) : null
      const when = this.$t('meter-device.connected-vehicle.place-detected-' + d.side, { m: mins })
      return `${this.fmtDist(d.distanceM)} · ${when}`
    },

    /** DRAFT (OBSERVED, still rebuilt) vs settled CONFIRMED (frozen, CDR-eligible) vs billed/ignored. */
    statusColor(status) {
      return {
        OBSERVED: undefined, // draft → outlined, no fill
        CONFIRMED: 'teal',
        CONVERTED: 'green',
        IGNORED: 'blue-grey',
      }[status]
    },

    /** Backend estimator enum → the i18n sub-keys already in use by the accuracy report. */
    estimatorLabelOf(enumKey) {
      const sub = {
        ENERGY_ADDED: 'energy-added',
        ENERGY_REMAINING: 'remaining',
        SOC: 'soc',
        POWER_DURATION: 'power-duration',
        BENCHMARK: 'benchmark',
      }[enumKey]
      return sub ? this.$t('meter-device.connected-vehicle.estimator.' + sub) : enumKey
    },

    // Infobulle d'une pastille d'estimateur : la nuance décimale du SoC, ou — pour le benchmark — la
    // branche qui a servi, sans quoi une valeur qui change de nature d'une charge à l'autre reste opaque.
    estimatorHint(e) {
      if (e.decimal) return this.$t('meter-device.connected-vehicle.soc-decimal-hint')
      if (e.key === 'BENCHMARK' && e.source) {
        return this.$t('meter-device.connected-vehicle.estimator.benchmark-' + e.source)
      }
      return undefined
    },

    estimatorLabel(k) {
      if (k === 'retainedKwh') {
        return this.$t('meter-device.connected-vehicle.retained-energy')
      }
      const sub = { energyAddedKwh: 'energy-added', socDeltaKwh: 'soc', energyRemainingDeltaKwh: 'remaining', powerDurationKwh: 'power-duration', benchmarkKwh: 'benchmark' }[k]
      return sub ? this.$t('meter-device.connected-vehicle.estimator.' + sub) : k
    },

    /** Compact duration: "4 h 07" above the hour, "42 min" below — a charge is read in hours, not in seconds. */
    fmtDuration(seconds) {
      if (seconds == null) return '—'
      const m = Math.round(seconds / 60)
      if (m < 60) return m + ' min'
      return Math.floor(m / 60) + ' h ' + String(m % 60).padStart(2, '0')
    },

    fmtKwh(v) {
      return (v === null || v === undefined) ? '—' : (Math.round(v * 1000) / 1000) + ' kWh'
    },

    fmtTime(v) {
      return new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    // The per-metric multiplicative coefficient in force (energyAdded/remaining/power), 1 when unset. NOT used for
    // SoC, which follows the additive model (grid = conversion·SoC + aux·duration) — see correctedValue.
    coeffFor(item, key) {
      const c = item.energy.coefficients && item.energy.coefficients[key]
      return (c == null) ? 1 : c
    },

    // Grid-side value of an estimator under the CURRENT model, computed server-side (SoC additive, rest ×coef).
    correctedValue(item, key, value) {
      if (value == null) return null
      const c = item.energy.corrected
      if (c && c[key] != null) return c[key]
      return value * this.coeffFor(item, key) // fallback for older payloads
    },

    // Human breakdown of the retained energy. For SoC with the duration-aware EV-Database model it shows
    // "conversion × SoC + P_aux kW × duration"; otherwise "base × factor". The applied rate is retainedKwh/base.
    retainedBreakdown(item) {
      const e = item.energy
      if (e.retainedBaseKwh == null || e.lossCoefficient == null) return null
      const m = e.socModel
      if (e.retainedEstimator === 'SOC' && m && m.mode === 'ev-db-duration') {
        const base = Math.round(e.retainedBaseKwh * 100) / 100
        const aux = Math.round((m.auxPower || 0) * (m.durationHours || 0) * 100) / 100
        return `${m.conversion} × ${base} + ${m.auxPower} kW × ${m.durationHours} h (= +${aux}) · EV DB`
      }
      const src = (e.retainedEstimator === 'SOC' && m && m.mode === 'ev-db-factor') ? ' (EV DB)' : ''
      return `${this.fmtKwh(e.retainedBaseKwh)} × ${e.lossCoefficient}${src}`
    },

    // True when the corrected (grid-side) value meaningfully differs from the raw estimator (a loss was applied).
    correctedDiffers(item, key, value) {
      const c = this.correctedValue(item, key, value)
      return c != null && value != null && Math.abs(c - value) > 0.01
    },

    // The borne reading describes the SAME period as the charge (window coverage ≥ 0.9). Below that, every
    // derived percentage is a comparison between different periods — suppressed rather than shown red.
    borneComparable(item) {
      // Server-side verdict: ≥90 % of the CHARGE is metered by the CDR(s), and the CDR is not essentially idle
      // tail. A CDR merely running on after the car is full stays a fair comparison and is NOT flagged.
      return item.energy.realChargerComparable === true
    },

    // Signed % gap of the CORRECTED estimator vs the real charger. Null when no borne matched this charge.
    gapPct(item, key, value) {
      if (!this.borneComparable(item)) return null
      const real = item.energy.realChargerKwh
      const corrected = this.correctedValue(item, key, value)
      if (real == null || real === 0 || corrected == null) return null
      return (corrected - real) / real * 100
    },

    // Green when within the ±2% target, red otherwise — the whole point of the study phase.
    gapClass(item, key, value) {
      const pct = this.gapPct(item, key, value)
      if (pct === null) return 'grey--text'
      return Math.abs(pct) <= 2 ? 'green--text' : 'red--text'
    },

    // Headline gap: the retained energy (already loss-corrected server-side) vs the borne.
    retainedGapPct(item) {
      if (!this.borneComparable(item)) return null
      const real = item.energy.realChargerKwh
      const ret = item.energy.retainedKwh
      if (real == null || real === 0 || ret == null) return null
      return (ret - real) / real * 100
    },

    fmtSignedPct(pct) {
      if (pct === null || pct === undefined) return '—'
      const r = Math.round(pct * 10) / 10
      return (r > 0 ? '+' : '') + r + '%'
    },

    // The loss coefficient is only meaningful for a battery-side retained estimator (the power integral is
    // already grid-side, coefficient forced to 1.0 → nothing to show).
    isBatterySideRetained(item) {
      return item.energy.retainedEstimator != null && item.energy.retainedEstimator !== 'POWER_DURATION'
    },

    // The coefficient that would make this very charge match the borne: borne ÷ battery-side base.
    idealCoefficient(item) {
      if (!this.borneComparable(item)) return null
      const real = item.energy.realChargerKwh
      const base = item.energy.retainedBaseKwh
      if (real == null || base == null || base === 0) return null
      return Math.round(real / base * 1000) / 1000
    },

    // A capture gap: the ideal coefficient is outside the physically-plausible loss band, i.e. the provider
    // only captured a slice of the charge (SmartCar's daily poll) or the match is spurious — not a loss.
    isCaptureGap(item) {
      const c = this.idealCoefficient(item)
      return c !== null && (c > 1.5 || c < 0.6)
    },

    fmtDist(m) {
      if (m === null || m === undefined) return '—'
      return m >= 1000 ? (Math.round(m / 100) / 10) + ' km' : Math.round(m) + ' m'
    },
  },
}
</script>

<style scoped>
/* Force readable dark text on the "to confirm" list — the table inherits a near-white cell colour otherwise
   (only visible on a dark hover row). ::v-deep + !important beat the inherited rule; the amber panel stays. */
.missing-list ::v-deep td { color: rgba(0, 0, 0, 0.82) !important; }
.missing-list ::v-deep th { color: rgba(0, 0, 0, 0.55) !important; }
.missing-list ::v-deep table { background: transparent !important; }
</style>
